import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { UnitInput } from '../ui/unit-input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Trash2, Plus, RotateCcw, Pencil, AlertTriangle } from 'lucide-react';
import { WorkCategoryWithPercentage, WorkCategory, BuildingType } from '../../types/index';
import { buildingTypeService } from '../../services/buildingTypeService';
import workCategoryService from '../../services/workCategoryService';

export interface WorkCategorySelectorData {
  buildingTypeName: string;
  workCategories: WorkCategoryWithPercentage[];
  isEdited: boolean;
}

interface WorkCategorySelectorProps {
  initialBuildingTypeName?: string;
  initialWorkCategories?: WorkCategoryWithPercentage[];
  totalEstimatedCost: number;
  onChange: (data: WorkCategorySelectorData) => void;
  savedProjectBuildingType?: {
    name: string;
    workCategories: WorkCategoryWithPercentage[];
  } | null;
}

const CUSTOM_VALUE = '___CUSTOM_EDITED_STATE___';
const SAVED_VALUE = '___SAVED_PROJECT_STATE___';

const WorkCategorySelector: React.FC<WorkCategorySelectorProps> = ({
  initialBuildingTypeName = '',
  initialWorkCategories = [],
  totalEstimatedCost,
  onChange,
  savedProjectBuildingType,
}) => {
  // Estado para los selects y datos
  const [buildingTypes, setBuildingTypes] = useState<BuildingType[]>([]);
  const [availableWorkCategories, setAvailableWorkCategories] = useState<WorkCategory[]>([]);

  // Estado de la selección actual
  const [selectedBuildingTypeName, setSelectedBuildingTypeName] = useState<string>(initialBuildingTypeName);
  const [selectedWorkCategories, setSelectedWorkCategories] = useState<WorkCategoryWithPercentage[]>(initialWorkCategories);
  const [originalTemplateMap, setOriginalTemplateMap] = useState<Record<string, WorkCategoryWithPercentage[]>>({});

  // Estado de carga y errores
  const [, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Estado para inputs temporales y edición
  const [addingRow, setAddingRow] = useState<{ workItemId: number | null; percentage: number }>({
    workItemId: null,
    percentage: 0,
  });

  // Helpers de redondeo y formato
  const roundTo = (value: number, decimals = 4) => {
    const factor = Math.pow(10, decimals);
    return Math.round((value + Number.EPSILON) * factor) / factor;
  };

  const moneyFormatter = useMemo(() => {
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }, []);

  const formatPercentageForUi = (value: number) => {
    return new Intl.NumberFormat('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 4,
    }).format(value);
  }

  // Helper para obtener el nombre base limpio (sin " personalizado")
  const getBaseName = useCallback((name: string) => {
    // Reemplaza una o más ocurrencias de " personalizado" al final, ignorando mayúsculas/minúsculas
    return name.replace(/(\s+personalizado)+$/i, '');
  }, []);

  // Carga inicial de datos (Tipos de Obra y Rubros Disponibles)
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [btResponse, wcResponse] = await Promise.all([
          buildingTypeService.getAllBuildingTypesWithWorkTypes(),
          workCategoryService.getAllWorkCategories()
        ]);

        // Procesar tipos de obra
        const types = Array.isArray(btResponse.data) ? btResponse.data : [];
        setBuildingTypes(types);

        // Crear mapa de plantillas originales para detectar cambios
        const templateMap: Record<string, WorkCategoryWithPercentage[]> = {};
        types.forEach(bt => {
          templateMap[bt.value] = bt.workCategories.map(wc => ({
            id: wc.id,
            value: wc.value || wcResponse.find(c => c.id === wc.id)?.value || '',
            description: wc.description || wcResponse.find(c => c.id === wc.id)?.description || '',
            percentage: wc.percentage
          }));
        });
        setOriginalTemplateMap(templateMap);

        // Procesar rubros disponibles
        setAvailableWorkCategories(wcResponse);

      } catch (err) {
        console.error('Error cargando datos:', err);
        setError('Error al cargar la configuración inicial.');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Helper de comparación profunda
  const areCategoriesEqual = useCallback((a: WorkCategoryWithPercentage[], b: WorkCategoryWithPercentage[]) => {
    if (a.length !== b.length) return false;
    return !a.some(catA => {
      const catB = b.find(c => c.id === catA.id);
      if (!catB) return true; // Diferente estructura
      return Math.abs(catA.percentage - catB.percentage) > 0.001; // Diferente porcentaje
    });
  }, []);

  // Calculada: ¿Está editado respecto a la plantilla seleccionada o el guardado?
  const isEdited = useMemo(() => {
    if (!selectedBuildingTypeName) return false;

    // 1. Intentar comparar con plantilla
    const baseName = getBaseName(selectedBuildingTypeName);
    const template = originalTemplateMap[baseName];

    if (template) {
      if (!areCategoriesEqual(selectedWorkCategories, template)) return true;
      // Si son iguales, es false (match exacto con plantilla)
      return false;
    }

    // 2. Si es algo totalmente nuevo manual, se considera editado solo si hay rubros
    return selectedWorkCategories.length > 0;
  }, [selectedBuildingTypeName, selectedWorkCategories, originalTemplateMap, areCategoriesEqual, getBaseName]);

  // Efecto para propagar cambios al padre
  useEffect(() => {
    let finalName = selectedBuildingTypeName;
    const baseName = getBaseName(selectedBuildingTypeName);
    const template = originalTemplateMap[baseName];

    // Solo agregamos " personalizado" si realmente hay un template base del que nos desviamos
    // Y verificamos que NO termine ya en " personalizado" (aunque getBaseName lo limpia, aquí chequeamos el current)
    const hasSuffix = /(\s+personalizado)+$/i.test(selectedBuildingTypeName);

    if (template && isEdited && !hasSuffix) {
      finalName = `${baseName} personalizado`;
    } else if (template && !isEdited && hasSuffix) {
      // Si volvió a coincidir con la plantilla, quitamos el sufijo para volver al nombre original
      finalName = baseName;
    }
    // Si no entra en los ifs, mantenemos el selectedBuildingTypeName actual 
    // (ej: ya tiene personalizado y sigue editado => se mantiene igual, no se agrega otro)

    onChange({
      buildingTypeName: finalName,
      workCategories: selectedWorkCategories,
      isEdited
    });
  }, [selectedBuildingTypeName, selectedWorkCategories, isEdited, onChange, originalTemplateMap, getBaseName]);


  // Handlers
  const handleBuildingTypeChange = (value: string) => {
    if (value === CUSTOM_VALUE) return; // No hacer nada si selecciona el label actual

    if (value === SAVED_VALUE) {
      if (savedProjectBuildingType) {
        setSelectedBuildingTypeName(savedProjectBuildingType.name);
        setSelectedWorkCategories(savedProjectBuildingType.workCategories);
      }
      return;
    }

    // Normal template selection
    const template = buildingTypes.find(bt => bt.value === value);
    if (template) {
      setSelectedBuildingTypeName(template.value);

      // Mapear categorias de la plantilla
      const newCategories = template.workCategories.map(wc => {
        // Asegurar tener datos completos
        const fullCat = availableWorkCategories.find(c => c.id === wc.id);
        return {
          id: wc.id,
          value: fullCat?.value || wc.value || '',
          description: fullCat?.description || wc.description || '',
          percentage: wc.percentage
        } as WorkCategoryWithPercentage;
      });

      setSelectedWorkCategories(newCategories);
    }
  };

  const persistRubroPorcentaje = (id: number, value: number) => {
    const next = roundTo(value);
    setSelectedWorkCategories(prev => prev.map(r => (r.id === id ? { ...r, percentage: next } : r)));
  };

  const handleDeleteRubro = (id: number) => {
    setSelectedWorkCategories(prev => prev.filter(r => r.id !== id));
  };

  const handleAddRubro = () => {
    if (addingRow.workItemId && addingRow.percentage > 0) {
      const workCategory = availableWorkCategories.find(wc => wc.id === addingRow.workItemId);
      if (workCategory && !selectedWorkCategories.some(s => s.id === workCategory.id)) {
        const newItem = { ...workCategory, percentage: roundTo(addingRow.percentage) } as WorkCategoryWithPercentage;
        setSelectedWorkCategories(prev => [...prev, newItem]);
        setAddingRow({ workItemId: null, percentage: 0 });
      }
    }
  };

  // Totales
  const totalRubrosPercentage = useMemo(() => {
    const total = selectedWorkCategories.reduce((sum, r) => sum + roundTo(r.percentage || 0), 0);
    return roundTo(total);
  }, [selectedWorkCategories]);

  const isRubrosSumValid = Math.abs(totalRubrosPercentage - 100) < 0.0001;

  // Helpers internos para visualizar en el select
  const getSelectValue = () => {
    // 0. Si no hay nada seleccionado
    if (!selectedBuildingTypeName && selectedWorkCategories.length === 0) {
      return ''; // Valor vacio para el placeholder
    }

    // 1. Check if matches saved state exactly (if exists)
    if (savedProjectBuildingType) {
      if (selectedBuildingTypeName === savedProjectBuildingType.name &&
        areCategoriesEqual(selectedWorkCategories, savedProjectBuildingType.workCategories)) {
        return SAVED_VALUE;
      }
    }

    // 2. Check if matches any template exactly
    const baseName = getBaseName(selectedBuildingTypeName);
    if (originalTemplateMap[baseName] && areCategoriesEqual(selectedWorkCategories, originalTemplateMap[baseName])) {
      // Encontrar el value correcto del template basado en el nombre base
      const template = buildingTypes.find(bt => bt.value === baseName);
      return template ? template.value : CUSTOM_VALUE;
    }

    // 3. Fallback -> custom state
    return CUSTOM_VALUE;
  };

  const currentSelectValue = getSelectValue();

  // Nombre base para mostrar en la interfaz (sin el tag repetido)
  const displayBaseName = getBaseName(selectedBuildingTypeName);

  return (
    <Card className="border-border">
      <CardHeader className="pb-3">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle className="text-lg font-semibold">Configuración de Rubros</CardTitle>
            <p className="text-sm text-muted-foreground">Seleccione el tipo de obra y ajuste los porcentajes.</p>
          </div>

          <div className="w-full sm:w-[350px]">
            <Select
              value={currentSelectValue}
              onValueChange={handleBuildingTypeChange}
            >
              <SelectTrigger className={currentSelectValue === CUSTOM_VALUE ? "border-orange-400 bg-orange-50/50" : ""}>
                <SelectValue placeholder="Seleccione una nueva obra">
                  {/* Visual custom display logic */}
                  {currentSelectValue === CUSTOM_VALUE ? (
                    <span className="flex items-center gap-2 font-medium text-orange-700">
                      {displayBaseName} <span className="text-xs opacity-70">(Personalizado)</span>
                    </span>
                  ) : currentSelectValue === SAVED_VALUE && savedProjectBuildingType ? (
                    <span>{savedProjectBuildingType.name} (Guardado)</span>
                  ) : (
                    selectedBuildingTypeName || "Seleccione una nueva obra"
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {/* Opción CUSTOM (Solo visible si estamos en ese estado) */}
                {currentSelectValue === CUSTOM_VALUE && (
                  <SelectItem value={CUSTOM_VALUE} className="font-medium text-orange-700 bg-orange-50 focus:bg-orange-100">
                    <span className="flex items-center gap-2">
                      <Pencil className="w-3.5 h-3.5" />
                      {displayBaseName} (Edición Actual)
                    </span>
                  </SelectItem>
                )}

                {/* Opción SAVED (Solo si existe) */}
                {savedProjectBuildingType && (
                  <>
                    <SelectItem value={SAVED_VALUE} className="font-medium text-blue-700 border-b bg-blue-50/30 focus:bg-blue-100">
                      <span className="flex items-center gap-2">
                        <RotateCcw className="w-3.5 h-3.5" />
                        Restaurar: {savedProjectBuildingType.name}
                      </span>
                    </SelectItem>
                  </>
                )}

                {/* Plantillas */}
                {buildingTypes.map(type => (
                  <SelectItem key={type.id} value={type.value}>
                    {type.value}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Indicador de estado Editado (Solo si hay selección inicial) */}
            {currentSelectValue === CUSTOM_VALUE && selectedBuildingTypeName && (
              <div className="flex items-center gap-1.5 mt-2 text-xs text-orange-600 font-medium">
                <AlertTriangle className="w-3.5 h-3.5" />
                <span>Estás editando una configuración personalizada.</span>
              </div>
            )}

            {/* Indicador de estado Guardado */}
            {currentSelectValue === SAVED_VALUE && savedProjectBuildingType && (
              <div className="flex items-center gap-1.5 mt-2 text-xs text-blue-600 font-medium">
                <span>★ Restaurado al estado guardado del proyecto</span>
              </div>
            )}
          </div>
        </div>
      </CardHeader>

      <div className="h-px bg-border" />

      <CardContent className="pt-6 space-y-4">
        {selectedBuildingTypeName ? (
          <>
            {/* Resumen de totales */}
            <div className="flex flex-col gap-2 p-3 border rounded sm:flex-row sm:items-center sm:justify-between bg-muted/20">
              <div className="text-sm">
                <span className="font-medium">Total asignado: </span>
                <span className={
                  isRubrosSumValid ? 'text-green-600 font-bold' :
                    totalRubrosPercentage > 100 ? 'text-red-600 font-bold' : 'text-orange-600 font-bold'
                }>
                  {formatPercentageForUi(totalRubrosPercentage)}%
                </span>
                {!isRubrosSumValid && (
                  <span className="ml-2 text-muted-foreground">
                    ({totalRubrosPercentage < 100 ? 'Faltan' : 'Excedido por'} {formatPercentageForUi(Math.abs(100 - totalRubrosPercentage))}%)
                  </span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">La suma debe ser 100%</div>
            </div>

            {error && <div className="p-2 text-sm text-red-600 rounded bg-red-50">{error}</div>}

            {/* Tabla de Rubros */}
            <div className="border rounded-md">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3 font-medium">Rubro</th>
                      <th className="px-4 py-3 font-medium text-center w-[150px]">Porcentaje</th>
                      <th className="px-4 py-3 font-medium text-right">Estimado</th>
                      <th className="px-4 py-3 font-medium text-right w-[100px]">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {selectedWorkCategories.map((cat) => (
                      <tr key={cat.id} className="group hover:bg-muted/20">
                        <td className="px-4 py-3 font-medium">{cat.value}</td>
                        <td className="px-4 py-3 text-center">
                          <UnitInput
                            unit="%"
                            value={cat.percentage}
                            onValueChange={(val) => {
                              if (val !== null) {
                                persistRubroPorcentaje(cat.id, val);
                              }
                            }}
                            className="h-8 text-right"
                            inputClassName="h-8 py-1"
                          />
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-muted-foreground">
                          ${moneyFormatter.format(((totalEstimatedCost || 0) * (cat.percentage || 0)) / 100)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="w-8 h-8 text-muted-foreground hover:text-red-600"
                            onClick={() => handleDeleteRubro(cat.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}

                    {/* Fila para agregar */}
                    <tr className="bg-muted/10">
                      <td className="px-4 py-2">
                        <Select
                          value={addingRow.workItemId ? String(addingRow.workItemId) : ''}
                          onValueChange={(val) => setAddingRow(prev => ({ ...prev, workItemId: parseInt(val) }))}
                        >
                          <SelectTrigger className="h-8">
                            <SelectValue placeholder="Agregar rubro..." />
                          </SelectTrigger>
                          <SelectContent>
                            {availableWorkCategories
                              .filter(opt => !selectedWorkCategories.some(s => s.id === opt.id))
                              .map(opt => (
                                <SelectItem key={opt.id} value={opt.id.toString()}>{opt.value}</SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-4 py-2 text-center">
                        {addingRow.workItemId && (
                          <UnitInput
                            unit="%"
                            value={addingRow.percentage ? addingRow.percentage.toString() : ''}
                            onValueChange={(val) => setAddingRow(prev => ({ ...prev, percentage: roundTo(val ?? 0) }))}
                            className="h-8 text-right"
                            inputClassName="h-8 py-1"
                            autoFocus
                          />
                        )}
                      </td>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2 text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8 gap-1"
                          onClick={handleAddRubro}
                          disabled={!addingRow.workItemId || !addingRow.percentage}
                        >
                          <Plus className="w-3.5 h-3.5" />
                          <span className="hidden sm:inline">Agregar</span>
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
            <p>Seleccione un <strong>Tipo de Obra</strong> para comenzar a configurar los rubros.</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default WorkCategorySelector;